import { memo, useEffect, useRef } from "react";

import { Box } from "@mui/material";

import { useBszPlannerContext } from "../../context/bszPlannerContext";
import { useAppContext } from "../../../../2_ksbHub/context/appContext";

import BszSectionCard from "../../../1_BSZFinderApp/components/sectionCards/BszSectionCard";

import usePlannerTimelineData from "./hooks/usePlannerTimelineData";

import TimelineRow from "./components/TimelineRow";

import {
  CELL_GAP,
  CELL_SIZE,
  LABEL_WIDTH,
} from "./constants/timelineConstants";

import { enrichTimelineProject } from "./utils/timelineUtils";

import dayjs from "dayjs";
import { SCHOOL_YEAR_2026_2027 } from "../../mockData/schoolYearData";
import { SUBJECT_LABELS } from "../../../1_BSZFinderApp/components/sectionCards/werHilftSection/config/subjectConfig";
import TimelineHeader from "./TimelineHeader";
import PlannerEmptyTimelineMessage from "./PlannerEmptyTimelineMessage";
import PlannerTimelineHelpMessage from "./PlannerTimelineHelpMessage";

function PlannerProjectTeacherTimelineSection({ dialogs, type }) {
  const {
    projects = [],
    subjects = [],
    myClasses = [],
  } = useBszPlannerContext();

  const { currentUser, isATeacher, isStudentView } = useAppContext();

  const schoolYearStart = dayjs(SCHOOL_YEAR_2026_2027.start).startOf("isoWeek");

  const myProjects = projects.filter(
    (project) => project.teacherId === currentUser.id,
  );

  const classRows = [];

  myClasses.forEach((cls) => {
    const projectsForClass = myProjects.filter((project) =>
      (project.classes || []).some((c) => c.id === cls.id),
    );

    const subjects = [...new Set(projectsForClass.map((p) => p.subjectName))];

    subjects.forEach((subjectName) => {
      classRows.push({
        classId: cls.id,

        label: `${cls.name} · ${SUBJECT_LABELS[subjectName] ?? subjectName}`,

        className: cls.name,
        subjectName,

        projects: projectsForClass
          .filter((p) => p.subjectName === subjectName)
          .map((project) => enrichTimelineProject(project, schoolYearStart)),
      });
    });
  });

  const hasProjects = classRows.length > 0;
  const shouldShowHelp = isATeacher && !isStudentView && myProjects.length < 3;

  const { weeks, holidayWeeks, currentWeekIndex } = usePlannerTimelineData({
    projects: myProjects,
    subjects,
    isTeacher: true,
    selectedClass: null,
  });

  const timelineScrollRef = useRef(null);

  useEffect(() => {
    if (!timelineScrollRef.current) {
      return;
    }

    if (currentWeekIndex < 0) {
      return;
    }

    timelineScrollRef.current.scrollLeft = Math.max(
      0,
      (currentWeekIndex - 4) * (CELL_SIZE + CELL_GAP),
    );
  }, [currentWeekIndex]);

  if (!hasProjects) {
    return (
      <BszSectionCard title="Meine Projekte · Timeline" defaultExpanded>
        <PlannerEmptyTimelineMessage hasClasses={myClasses.length > 0} />
      </BszSectionCard>
    );
  }
  return (
    <BszSectionCard title="Meine Projekte · Timeline" defaultExpanded>
      {shouldShowHelp && <PlannerTimelineHelpMessage />}
      <Box
        ref={timelineScrollRef}
        sx={{
          height: 500,
          overflow: "auto",

          scrollbarWidth: "none", // Firefox
          msOverflowStyle: "none", // IE/Edge

          "&::-webkit-scrollbar": {
            display: "none", // Chrome/Safari
          },
        }}
      >
        <Box
          sx={{
            width: LABEL_WIDTH + weeks.length * (CELL_SIZE + CELL_GAP),
          }}
        >
          <TimelineHeader
            weeks={weeks}
            holidayWeeks={holidayWeeks}
            labelWidth={type === "teacherOwnProjectView" ? 120 : LABEL_WIDTH}
          />

          {/* ===========================================
                CLASS ROWS
            =========================================== */}

          {classRows.map((row) => (
            <TimelineRow
              key={`${row.classId}-${row.subjectName}`}
              row={row}
              weeks={weeks}
              holidayWeeks={holidayWeeks}
              dialogs={dialogs}
              type={type}
            />
          ))}
        </Box>
      </Box>
    </BszSectionCard>
  );
}

export default memo(PlannerProjectTeacherTimelineSection);
